/**
 * TrackFlytLive — Track a live college-football game on the iOS
 * lock screen via ActivityKit + a matching in-app glass scoreboard.
 *
 * Exposes three surfaces:
 *
 *   • <TrackFlytLiveButton />        — the small "Track FLYT Live" pill
 *                                      that sits at the bottom-center of
 *                                      an athlete / network page. On
 *                                      web this just toggles the
 *                                      in-app scoreboard; on native iOS
 *                                      it opens the Uber-style
 *                                      permission sheet + starts the
 *                                      Live Activity.
 *
 *   • <TrackFlytLivePermissionSheet /> — the Uber-styled bottom sheet
 *                                      that explains what the user is
 *                                      opting into ("Follow this game
 *                                      on your Lock Screen"), asks for
 *                                      the ActivityKit push token, and
 *                                      registers it with the backend.
 *
 *   • <LiveGameGlassCard />          — an in-app scoreboard card that
 *                                      mirrors the widget design so the
 *                                      user has instant feedback + the
 *                                      web experience has parity.
 *
 * Native bridge: on iOS Capacitor we call
 *   `window.Capacitor.Plugins.FlytLiveActivity.start(...)`
 * which our custom Swift plugin (see `docs/ios/LiveActivity.md`) uses to
 * start an Activity via `Activity<FlytGameAttributes>.request(...)`. The
 * plugin returns the initial push token, which we forward to
 * `POST /api/live-activity/register-token`.
 *
 * On web the same button drops a purely-visual "Live scoreboard" glass
 * card into the page so the demo works without native support.
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Bell, Zap, ChevronRight, Play, Circle, HelpCircle } from 'lucide-react';
import { isNativeApp, haptic } from '../../lib/nativeBridge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ── Native bridge helpers ────────────────────────────────────────────

async function startNativeLiveActivity(game) {
  // Call the custom Swift plugin registered as `FlytLiveActivity`.
  // Returns `{ activityId, pushToken }` when ActivityKit successfully
  // hands back an initial token. Bails gracefully on web.
  if (!isNativeApp()) return null;
  try {
    const { Capacitor } = await import('@capacitor/core');
    const plugin = Capacitor.Plugins?.FlytLiveActivity;
    if (!plugin?.start) throw new Error('FlytLiveActivity plugin missing');
    const res = await plugin.start({
      gameId: game.game_id || game.id,
      home: game.home || 'HOME',
      away: game.away || 'AWAY',
      teamName: game.team_name || game.home,
      opponent: game.opponent || game.away,
      kickoffIso: game.kickoff_iso || '',
    });
    return res || null;
  } catch (err) {
    throw new Error(err?.message || 'Native Live Activity failed to start.');
  }
}

async function stopNativeLiveActivity(gameId) {
  if (!isNativeApp()) return;
  try {
    const { Capacitor } = await import('@capacitor/core');
    const plugin = Capacitor.Plugins?.FlytLiveActivity;
    if (plugin?.stop) await plugin.stop({ gameId });
  } catch { /* best-effort */ }
}


// ── Permission sheet (Uber-style) ────────────────────────────────────

export function TrackFlytLivePermissionSheet({ game, onClose, onGranted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // iter348 · Multi-game block flow — after Allow tap we fetch the
  // current live scoreboard, and if any other in-progress games exist
  // we show a second step where the user can pick up to 3 more games
  // to render underneath the primary score on the Lock Screen.
  const [step, setStep] = useState('primary'); // 'primary' | 'more' | 'limit'
  const [otherLive, setOtherLive] = useState([]);
  const [selected, setSelected] = useState({}); // { gameId: true }
  const [limitGames, setLimitGames] = useState([]); // current 5 when capped
  const [pendingExtra, setPendingExtra] = useState([]);

  const primaryGameKey = game?.game_id || game?.id;

  // Kick off the actual Live Activity start — used from both the
  // "primary only" fast path and the "primary + N other games" path.
  const startLiveActivity = async (extraGames = []) => {
    // 1. Register the local track in the backend regardless of native
    //    support so the in-app scoreboard has a row to render. If the
    //    account is already at the 5-game cap, the API returns 409 with
    //    the current list — surface a picker instead of failing.
    try {
      await axios.post(`${API}/live-games/track`, {
        game_id: primaryGameKey,
        team_slug: game.team_slug,
        team_name: game.team_name,
        opponent: game.opponent,
        kickoff_iso: game.kickoff_iso,
        home: game.home,
        away: game.away,
        sport: 'college_football',
      }, { withCredentials: true });
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        const err = new Error('tracked_limit_reached');
        err.code = 'limit';
        err.games = detail.games || [];
        throw err;
      }
      throw e;
    }

    let nativeWorked = false;
    let nativeErrMsg = '';
    if (isNativeApp()) {
      try {
        const { Capacitor } = await import('@capacitor/core');
        const plugin = Capacitor.Plugins?.FlytLiveActivity;
        if (!plugin?.start) {
          nativeErrMsg = 'FlytLiveActivity plugin missing (Capacitor.Plugins.FlytLiveActivity undefined)';
        } else {
          const handle = await plugin.addListener?.('liveActivityToken', async (evt) => {
            if (!evt || evt.gameId !== primaryGameKey) return;
            try {
              await axios.post(`${API}/live-activity/register-token`, {
                game_id: primaryGameKey,
                activity_id: evt.activityId,
                activity_token: evt.pushToken,
              }, { withCredentials: true });
            } catch { /* backend will retry via cron */ }
            // iter · Do NOT remove the listener after the first token.
            // ActivityKit rotates a Live Activity's push token during a
            // game; if we stop listening after the first one the backend
            // keeps pushing to a stale token and updates die mid-game.
            // Keeping it open re-registers every rotated token so scores
            // keep flowing until the activity ends.
          });

          const nativeRes = await plugin.start({
            gameId: primaryGameKey,
            home: game.home || 'HOME',
            away: game.away || 'AWAY',
            teamName: game.team_name || game.home,
            opponent: game.opponent || game.away,
            kickoffIso: game.kickoff_iso || '',
            // iter694 · Seed initial live score so the Lock Screen card
            // shows the real number instantly (backend refreshes /10s).
            homeScore: Number(game.home_score) || 0,
            awayScore: Number(game.away_score) || 0,
            clock: game.clock || '',
            period: Number(game.period) || 0,
            status: game.status || 'scheduled',
            sport: game.sport || 'cfb',
            otherGames: extraGames,
          });
          if (nativeRes?.activityId) {
            nativeWorked = true;
            if (nativeRes.pushToken) {
              try {
                await axios.post(`${API}/live-activity/register-token`, {
                  game_id: primaryGameKey,
                  activity_id: nativeRes.activityId,
                  activity_token: nativeRes.pushToken,
                }, { withCredentials: true });
              } catch { /* listener will handle */ }
            }
          } else {
            nativeErrMsg = 'Plugin returned no activityId';
          }
        }
      } catch (nativeErr) {
        nativeErrMsg = nativeErr?.message || String(nativeErr) || 'unknown';
        console.warn('[FlytLive] native start failed:', nativeErrMsg);
      }
    }
    return { nativeWorked, nativeErrMsg };
  };

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      // iter348 · Peek at other in-progress games BEFORE we start the
      // Activity. If there are candidates we route the user through
      // step 2 (multi-select). If nothing else is live we fast-path
      // straight to the Live Activity start.
      let candidates = [];
      try {
        const res = await axios.get(`${API}/scoreboard`);
        const rawGames = (res.data?.games || []).filter((g) =>
          g?.status === 'in' && String(g?.id || '') !== String(primaryGameKey || '')
        );
        candidates = rawGames.slice(0, 16).map((g) => ({
          id: String(g.id),
          label: `${(g.away?.name || 'AWY')} ${g.away?.score || 0} · ${(g.home?.name || 'HME')} ${g.home?.score || 0}`,
          status: g.status_text || 'Live',
          league: g.league,
        }));
      } catch { /* scoreboard optional */ }

      if (candidates.length > 0) {
        setOtherLive(candidates);
        setStep('more');
        setBusy(false);
        return;
      }
      // iter363 · Even when no other games are live right now, ALWAYS
      // route through the picker step so the flow feels consistent.
      // The picker renders an empty state with a "Just track this
      // game" CTA — this matches the user's mental model ("I remember
      // being able to add other games") and gracefully handles the
      // pre-game window when the scoreboard is empty.
      setOtherLive([]);
      setStep('more');
      setBusy(false);
      return;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Could not start tracking.';
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmMore = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      const picked = otherLive
        .filter((g) => selected[g.id])
        .slice(0, 8)
        .map((g) => ({ label: g.label, status: g.status, active: true }));
      const { nativeWorked, nativeErrMsg } = await startLiveActivity(picked);
      // iter481 · Big "boom, it's on the Lock Screen" moment.
      haptic('success');
      toast.success(picked.length ? `Tracking ${picked.length + 1} games` : 'Tracking this game', {
        description: nativeWorked
          ? (picked.length
              ? 'Your primary game + ' + picked.length + ' more will show on your Lock Screen.'
              : 'Score updates will appear on your Lock Screen.')
          : (isNativeApp()
              ? `In-app tracking active. Lock-Screen widget didn't start — reason: ${nativeErrMsg || 'unknown'}. Check Settings → Flyt House → Live Activities.`
              : 'Score updates will appear inside the app.'),
        duration: nativeWorked ? 4000 : 9000,
      });
      onGranted?.();
      onClose?.();
    } catch (e) {
      if (e?.code === 'limit') {
        // At the 5-game cap — show the "remove one or cancel" picker.
        setLimitGames(e.games || []);
        setPendingExtra(otherLive.filter((g) => selected[g.id]).slice(0, 8)
          .map((g) => ({ label: g.label, status: g.status, active: true })));
        setStep('limit');
        setBusy(false);
        return;
      }
      const msg = e?.response?.data?.detail || e?.message || 'Could not start tracking.';
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Limit picker: remove one existing tracked game, then retry the start.
  const removeAndRetry = async (gid) => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await axios.delete(`${API}/live-games/track/${gid}`, { withCredentials: true });
      const { nativeWorked } = await startLiveActivity(pendingExtra);
      haptic('success');
      toast.success('Tracking this game', {
        description: nativeWorked ? 'Score updates will appear on your Lock Screen.' : 'Score updates will appear inside the app.',
        duration: 4000,
      });
      onGranted?.();
      onClose?.();
    } catch (e) {
      const msg = e?.code === 'limit' ? 'Still at the 5-game limit — remove one more.' : (e?.message || 'Could not start tracking.');
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const stopScroll = () => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  };
  useEffect(stopScroll, []);

  const sheet = (
    <div
      className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="track-flyt-live-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-[28px] bg-neutral-950 ring-1 ring-white/10 p-6 pb-[max(24px,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300"
        style={{
          background:
            'radial-gradient(120% 100% at 50% 0%, rgba(96,165,250,0.16) 0%, rgba(0,0,0,0) 55%), #0A0A0C',
        }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-white/25 mb-6" />

        {step === 'primary' && (
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-blue-500/15 ring-1 ring-blue-400/40">
                  <Zap className="w-5 h-5 text-blue-300" strokeWidth={2.4} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">FLYT Live</p>
                  <h3 className="text-white text-xl font-black tracking-tight">Follow this game live</h3>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-full bg-white/10 inline-flex items-center justify-center text-white/80 hover:bg-white/20 transition"
                data-testid="track-flyt-live-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-3 mb-5">
              <p className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Game</p>
              <p className="text-white font-bold text-sm">
                {game?.home || 'Home'} <span className="text-white/40 mx-1">vs</span> {game?.away || 'Away'}
              </p>
              {game?.kickoff_iso && (
                <p className="text-white/60 text-xs mt-0.5">Kickoff {new Date(game.kickoff_iso).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}</p>
              )}
            </div>

            <ul className="space-y-3 mb-6">
              <PermRow icon={Bell} title="Lock-screen score updates" body="See scores + game clock without opening the app." />
              <PermRow icon={Circle} title="Live in the Dynamic Island" body="Long-press to expand into a full box score." />
              <PermRow icon={Zap} title="Big-play alerts" body="TDs, red-zone plays, and final scores buzz your phone." />
            </ul>

            {err && (
              <p className="text-red-400 text-xs mb-3 text-center" data-testid="track-flyt-live-error">{err}</p>
            )}

            <button
              type="button"
              onClick={accept}
              disabled={busy}
              className="w-full py-3.5 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-100 transition active:scale-[0.99] disabled:opacity-60 mb-2"
              data-testid="track-flyt-live-allow"
            >
              {busy ? 'Turning on…' : 'Allow Lock-Screen Notifications'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 text-white/60 text-sm hover:text-white transition"
              data-testid="track-flyt-live-not-now"
            >
              Not now
            </button>
          </>
        )}
        {step === 'more' && (
          <>
            {/* iter348 · Step 2 — "Also track these live games?" */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">FLYT Live · Multi-game</p>
                <h3 className="text-white text-xl font-black tracking-tight">Also track these games?</h3>
                <p className="text-white/55 text-xs mt-1">Pick up to 8. They&rsquo;ll show under your primary game on the Lock Screen.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-full bg-white/10 inline-flex items-center justify-center text-white/80 hover:bg-white/20 transition"
                data-testid="track-flyt-live-more-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[280px] overflow-y-auto space-y-2 mb-4 pr-1" data-testid="track-flyt-live-more-list">
              {otherLive.length === 0 && (
                /* iter363 · Empty state — no live games elsewhere right
                   now. Common in pre-game and post-final windows.
                   Athlete/fan still gets a clear "Just this game"
                   confirm below. */
                <div
                  className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-6 text-center"
                  data-testid="track-flyt-live-more-empty"
                >
                  <p className="text-white text-sm font-bold">No other games live right now</p>
                  <p className="text-white/55 text-xs mt-1.5 leading-relaxed">
                    We&rsquo;ll still ping your Lock Screen when a game elsewhere kicks off
                    while you&rsquo;re tracking. Tap below to lock in this game.
                  </p>
                </div>
              )}
              {otherLive.map((g) => {
                const isPicked = !!selected[g.id];
                const pickedCount = Object.values(selected).filter(Boolean).length;
                const disabled = !isPicked && pickedCount >= 8;
                return (
                  <button
                    key={g.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => setSelected((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                    className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 text-left transition ${isPicked
                      ? 'bg-blue-500/20 ring-blue-400/60'
                      : 'bg-white/5 ring-white/10 hover:bg-white/10'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid={`track-flyt-live-more-${g.id}`}
                  >
                    <span className={`w-5 h-5 rounded-full ring-1 flex items-center justify-center flex-shrink-0 ${isPicked ? 'bg-blue-400 ring-blue-300' : 'ring-white/30'}`}>
                      {isPicked && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-white text-sm font-bold truncate">{g.label}</span>
                      <span className="block text-white/50 text-[10px] uppercase tracking-wider font-semibold mt-0.5">
                        {g.league} · {g.status}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold text-red-400 uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
                    </span>
                  </button>
                );
              })}
            </div>

            {err && (
              <p className="text-red-400 text-xs mb-3 text-center" data-testid="track-flyt-live-more-error">{err}</p>
            )}

            <button
              type="button"
              onClick={confirmMore}
              disabled={busy}
              className="w-full py-3.5 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-100 transition active:scale-[0.99] disabled:opacity-60 mb-2"
              data-testid="track-flyt-live-more-confirm"
            >
              {busy
                ? 'Turning on…'
                : (Object.values(selected).filter(Boolean).length > 0
                    ? `Track ${Object.values(selected).filter(Boolean).length + 1} games`
                    : 'Just this game')}
            </button>
            <button
              type="button"
              onClick={() => setStep('primary')}
              className="w-full py-3 text-white/60 text-sm hover:text-white transition"
              data-testid="track-flyt-live-more-back"
            >
              Back
            </button>
          </>
        )}
        {step === 'limit' && (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold">FLYT Live · 5-game limit</p>
                <h3 className="text-white text-xl font-black tracking-tight">You&rsquo;re tracking 5 games</h3>
                <p className="text-white/55 text-xs mt-1">Remove one to make room, or cancel.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-full bg-white/10 inline-flex items-center justify-center text-white/80 hover:bg-white/20 transition"
                data-testid="track-flyt-live-limit-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 mb-4" data-testid="track-flyt-live-limit-list">
              {limitGames.map((g) => (
                <div
                  key={g.game_id}
                  className="flex items-center gap-3 rounded-2xl px-4 py-3 bg-white/5 ring-1 ring-white/10"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-white text-sm font-bold truncate">
                      {(g.home || 'Home')} vs {(g.away || 'Away')}
                    </span>
                    <span className="block text-white/45 text-[10px] uppercase tracking-wider font-semibold mt-0.5">
                      {g.is_primary ? 'Primary' : 'Tracking'}
                    </span>
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => removeAndRetry(g.game_id)}
                    className="px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-bold hover:bg-white/80 transition disabled:opacity-50"
                    data-testid={`track-flyt-live-limit-remove-${g.game_id}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            {err && (
              <p className="text-white/70 text-xs mb-3 text-center" data-testid="track-flyt-live-limit-error">{err}</p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 text-white/60 text-sm hover:text-white transition"
              data-testid="track-flyt-live-limit-cancel"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheet, document.body);
}

const PermRow = ({ icon: Icon, title, body }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 inline-flex w-8 h-8 rounded-full bg-white/8 ring-1 ring-white/10 items-center justify-center shrink-0">
      <Icon className="w-4 h-4 text-blue-300" />
    </span>
    <div>
      <p className="text-white font-bold text-sm leading-tight">{title}</p>
      <p className="text-white/60 text-xs mt-0.5 leading-snug">{body}</p>
    </div>
  </li>
);


// ── Button ───────────────────────────────────────────────────────────

export function TrackFlytLiveButton({ game, className = '', variant = 'floating' }) {
  const [open, setOpen] = useState(false);
  const [tracked, setTracked] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Cheap lookup — if the user already tracks this game, show the "on" state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/live-games/tracked`, { withCredentials: true });
        if (cancelled) return;
        const items = r?.data?.games || r?.data?.items || [];
        const gid = game?.game_id || game?.id;
        setTracked(items.some((t) => (t.game_id === gid)));
      } catch { /* anonymous — no track state */ }
    })();
    return () => { cancelled = true; };
  }, [game]);

  const untrack = useCallback(async () => {
    try {
      const gid = game?.game_id || game?.id;
      await axios.delete(`${API}/live-games/track/${gid}`, { withCredentials: true });
      await stopNativeLiveActivity(gid);
      setTracked(false);
      toast.success('Stopped tracking');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not stop tracking.');
    }
  }, [game]);

  const style = variant === 'floating'
    ? 'fixed bottom-[calc(88px+env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2 z-40'
    : 'inline-flex';

  return (
    <>
      {/* iter331/iter332 · Apple-style liquid-glass pill.
          Softer than v1: lower opacity glass, gentler emerald halo
          that just barely breathes, and a green Zap icon that keeps
          the subtle pulse. Sits next to a tiny "?" chip that pops a
          one-line explainer for first-time users. */}
      <style>{`
        @keyframes flyt-live-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.28), inset 0 1px 0 rgba(255,255,255,0.06); }
          50%      { box-shadow: 0 0 0 6px rgba(52, 211, 153, 0.00), inset 0 1px 0 rgba(255,255,255,0.06); }
        }
        @keyframes flyt-live-dot {
          0%, 100% { opacity: 0.85; transform: scale(1); filter: drop-shadow(0 0 3px rgba(52,211,153,0.55)); }
          50%      { opacity: 1;    transform: scale(1.08); filter: drop-shadow(0 0 6px rgba(52,211,153,0.85)); }
        }
        .flyt-live-pill {
          animation: flyt-live-pulse 3.2s ease-in-out infinite;
        }
        .flyt-live-pill-dot {
          animation: flyt-live-dot 1.8s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .flyt-live-pill, .flyt-live-pill-dot { animation: none; }
        }
      `}</style>
      <div className={`${style} ${className} items-center gap-2`}>
        <button
          type="button"
          onClick={() => { haptic('medium'); return tracked ? untrack() : setOpen(true); }}
          className={`flyt-live-pill inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-white/95 transition active:scale-[0.97] ${tracked
            ? 'ring-1 ring-emerald-400/35'
            : 'ring-1 ring-emerald-400/25 hover:ring-emerald-300/45'}`}
          style={{
            background: 'linear-gradient(180deg, rgba(24,26,30,0.45) 0%, rgba(10,11,14,0.55) 100%)',
            backdropFilter: 'blur(22px) saturate(150%)',
            WebkitBackdropFilter: 'blur(22px) saturate(150%)',
          }}
          data-testid="track-flyt-live-btn"
        >
          {tracked ? (
            <>
              <Circle className="flyt-live-pill-dot w-2.5 h-2.5 fill-emerald-400 text-emerald-400" />
              <span>Tracking · Tap to stop</span>
            </>
          ) : (
            <>
              <Zap className="flyt-live-pill-dot w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
              <span>Track FLYT Live</span>
            </>
          )}
        </button>

        {/* iter332 · One-tap explainer chip. Small enough not to
            crowd the pill, big enough to be tappable on mobile.
            Toggles a lightweight tooltip that first-time users
            need to understand what the button actually does. */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setHelpOpen(v => !v)}
            aria-label="What does Track FLYT Live do?"
            aria-expanded={helpOpen}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white/70 hover:text-white transition ring-1 ring-white/15 hover:ring-white/30"
            style={{
              background: 'rgba(24,26,30,0.4)',
              backdropFilter: 'blur(18px) saturate(150%)',
              WebkitBackdropFilter: 'blur(18px) saturate(150%)',
            }}
            data-testid="track-flyt-live-help-btn"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
          {helpOpen && (
            <>
              {/* Invisible dismiss layer */}
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                aria-label="Close explainer"
                className="fixed inset-0 z-[70] cursor-default"
                data-testid="track-flyt-live-help-dismiss"
              />
              <div
                role="tooltip"
                className="absolute z-[71] top-full mt-2 right-0 w-64 sm:w-72 rounded-2xl px-4 py-3 text-left ring-1 ring-white/15"
                style={{
                  background: 'linear-gradient(180deg, rgba(20,22,26,0.92) 0%, rgba(10,11,14,0.96) 100%)',
                  backdropFilter: 'blur(24px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(160%)',
                  boxShadow: '0 20px 45px -20px rgba(0,0,0,0.65)',
                }}
                data-testid="track-flyt-live-help-tip"
              >
                <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-1">What is this?</p>
                <p className="text-white/90 text-[12px] leading-snug">
                  Add this game to your Lock Screen &amp; Home Screen so you can follow the live score without opening the app.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      {open && (
        <TrackFlytLivePermissionSheet
          game={game}
          onClose={() => setOpen(false)}
          onGranted={() => setTracked(true)}
        />
      )}
    </>
  );
}


// ── In-app scoreboard (glass, mirrors the widget) ────────────────────

export function LiveGameGlassCard({ game, onClose, onStop }) {
  const [live, setLive] = useState(game);

  // Poll the scoreboard every 15s while mounted.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/live-games/scoreboard`, { withCredentials: true });
        if (!alive) return;
        const hit = (r?.data?.items || []).find((g) => g.game_id === (game.game_id || game.id));
        if (hit) setLive({ ...game, ...hit });
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, [game]);

  const homeScore = live?.home_score ?? 0;
  const awayScore = live?.away_score ?? 0;
  const winning = homeScore === awayScore ? null : homeScore > awayScore ? 'home' : 'away';

  return (
    <div
      className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 p-5"
      style={{
        background:
          'radial-gradient(140% 90% at 0% 0%, rgba(96,165,250,0.18) 0%, rgba(0,0,0,0) 55%), radial-gradient(140% 90% at 100% 100%, rgba(147,197,253,0.10) 0%, rgba(0,0,0,0) 60%), rgba(10,10,12,0.95)',
        backdropFilter: 'blur(24px) saturate(140%)',
        WebkitBackdropFilter: 'blur(24px) saturate(140%)',
      }}
      data-testid="live-game-glass-card"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-blue-300">
          <Circle className="w-2.5 h-2.5 fill-blue-300 animate-pulse" /> FLYT Live · {live?.status === 'in_progress' ? 'Live' : 'Scheduled'}
        </span>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full bg-white/10 inline-flex items-center justify-center hover:bg-white/20 transition">
            <X className="w-3.5 h-3.5 text-white/80" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 items-center gap-3">
        <Team label={live?.home} score={homeScore} winning={winning === 'home'} align="left" />
        <div className="text-center">
          <p className="text-white/50 text-[9px] uppercase tracking-widest font-bold">Q{live?.period || 1}</p>
          <p className="text-white font-black text-lg tabular-nums leading-none">{live?.clock || '—:—'}</p>
        </div>
        <Team label={live?.away} score={awayScore} winning={winning === 'away'} align="right" />
      </div>

      <div className="mt-4 flex items-center justify-between text-[11px]">
        <button
          type="button"
          onClick={() => onStop?.()}
          className="text-white/60 hover:text-white transition"
          data-testid="live-game-glass-stop"
        >
          Stop tracking
        </button>
        <span className="text-white/40">
          Updated {live?.updated_at ? new Date(live.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
        </span>
      </div>
    </div>
  );
}

const Team = ({ label, score, winning, align = 'left' }) => (
  <div className={align === 'right' ? 'text-right' : 'text-left'}>
    <p className="text-white/70 text-[11px] uppercase tracking-widest font-bold truncate">{label || '—'}</p>
    <p className={`font-black text-3xl tabular-nums leading-none mt-1 ${winning ? 'text-white' : 'text-white/70'}`}>{score}</p>
  </div>
);


// ── FLYT LIVE multi-game sheet ───────────────────────────────────────
// Black-and-white minimal sheet listing every game the account tracks.
// Tap a game to make it primary; tap Remove to untrack just that game.
export function FlytLiveMultiGameSheet({ onClose }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/live-games/tracked`, { withCredentials: true });
      setGames(r?.data?.games || []);
      setLoadError(false);
    } catch { setGames([]); setLoadError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const setPrimary = async (gid) => {
    if (busy) return;
    setBusy(true); haptic('light');
    try {
      await axios.patch(`${API}/live-games/track/${gid}/primary`, {}, { withCredentials: true });
      await load();
    } catch { toast.error('Could not switch primary'); }
    finally { setBusy(false); }
  };

  const remove = async (gid) => {
    if (busy) return;
    setBusy(true); haptic('medium');
    try {
      await axios.delete(`${API}/live-games/track/${gid}`, { withCredentials: true });
      await stopNativeLiveActivity(gid);
      await load();
      toast.success('Removed from FLYT Live');
    } catch { toast.error('Could not remove'); }
    finally { setBusy(false); }
  };

  const sheet = (
    <div
      className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="flyt-live-multigame-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-[28px] bg-neutral-950 ring-1 ring-white/10 p-6 pb-[max(24px,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300"
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-white/25 mb-6" />
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-black">FLYT LIVE</p>
            <h3 className="text-white text-xl font-black tracking-tight">Your tracked games</h3>
            <p className="text-white/50 text-xs mt-1" data-testid="flyt-live-multigame-count">
              {games.length} of 5 · tap a game to make it primary
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-white/10 inline-flex items-center justify-center text-white/80 hover:bg-white/20 transition"
            data-testid="flyt-live-multigame-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <p className="text-white/50 text-sm py-8 text-center">Loading…</p>
        ) : loadError ? (
          <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-8 text-center" data-testid="flyt-live-multigame-error">
            <p className="text-white text-sm font-bold">Couldn&rsquo;t load your games</p>
            <p className="text-white/50 text-xs mt-1.5">Check your connection or sign in again, then reopen FLYT Live.</p>
          </div>
        ) : games.length === 0 ? (
          <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 px-4 py-8 text-center" data-testid="flyt-live-multigame-empty">
            <p className="text-white text-sm font-bold">No games tracked yet</p>
            <p className="text-white/50 text-xs mt-1.5">Tap “Track FLYT Live” on any live game to add it here.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="flyt-live-multigame-list">
            {games.map((g) => (
              <div
                key={g.game_id}
                className={`flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 transition ${g.is_primary ? 'bg-white/[0.10] ring-white/40' : 'bg-white/5 ring-white/10'}`}
                data-testid={`flyt-live-multigame-row-${g.game_id}`}
              >
                <button
                  type="button"
                  onClick={() => !g.is_primary && setPrimary(g.game_id)}
                  disabled={busy}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`flyt-live-multigame-primary-${g.game_id}`}
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${g.status === 'watching' ? 'bg-white' : 'bg-white/30'}`} />
                    <span className="block text-white text-sm font-black truncate">
                      {(g.home || 'Home')} <span className="text-white/40">vs</span> {(g.away || 'Away')}
                    </span>
                  </span>
                  <span className="block text-white/45 text-[10px] uppercase tracking-[0.15em] font-bold mt-1 ml-4">
                    {g.is_primary ? '★ Primary' : 'Tap to make primary'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => remove(g.game_id)}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-full bg-white/10 text-white text-[11px] font-bold hover:bg-white/20 transition disabled:opacity-50"
                  data-testid={`flyt-live-multigame-remove-${g.game_id}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheet, document.body);
}


export default TrackFlytLiveButton;
